// HorizontalMonthlyStats.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function HorizontalMonthlyStats({ userData }) {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Meses del año
  const months = [
    { id: '01', name: 'Ene', fullName: 'Enero' },
    { id: '02', name: 'Feb', fullName: 'Febrero' },
    { id: '03', name: 'Mar', fullName: 'Marzo' },
    { id: '04', name: 'Abr', fullName: 'Abril' },
    { id: '05', name: 'May', fullName: 'Mayo' },
    { id: '06', name: 'Jun', fullName: 'Junio' },
    { id: '07', name: 'Jul', fullName: 'Julio' },
    { id: '08', name: 'Ago', fullName: 'Agosto' },
    { id: '09', name: 'Sep', fullName: 'Septiembre' },
    { id: '10', name: 'Oct', fullName: 'Octubre' },
    { id: '11', name: 'Nov', fullName: 'Noviembre' },
    { id: '12', name: 'Dic', fullName: 'Diciembre' }
  ];

  // Obtener datos del mes
  const getMonthData = (monthId) => {
    const currentYear = new Date().getFullYear();
    const monthKey = `${currentYear}_${monthId}`;
    const hasData = userData && userData[monthKey];
    const currentMonth = new Date().getMonth() + 1;
    const isFutureMonth = parseInt(monthId) > currentMonth;

    if (isFutureMonth) {
      return {
        hasData: false,
        isFuture: true,
        overallScore: 0,
        monthName: months.find(m => m.id === monthId)?.fullName || '',
        level: 'futuro'
      };
    }

    if (!hasData) {
      return {
        hasData: false,
        isFuture: false,
        overallScore: 0,
        monthName: months.find(m => m.id === monthId)?.fullName || '',
        level: 'sin-datos'
      };
    }

    const monthData = userData[monthKey];
    const inorm = monthData.hl || 0;
    const comprehension = monthData.cl || 0;
    const overallScore = (inorm + comprehension) / 2;

    // Determinar nivel según el puntaje
    let level = 'inicio';
    let levelLabel = 'Inicio';
    
    if (overallScore >= 0.9) {
      level = 'destacado';
      levelLabel = 'Destacado';
    } else if (overallScore >= 0.8) {
      level = 'logrado';
      levelLabel = 'Logrado';
    } else if (overallScore >= 0.65) {
      level = 'progreso';
      levelLabel = 'En Progreso';
    } else if (overallScore >= 0.45) {
      level = 'inicio';
      levelLabel = 'Inicio';
    } else {
      level = 'inicio';
      levelLabel = 'Inicio';
    }

    return {
      hasData: true,
      isFuture: false,
      overallScore,
      monthName: months.find(m => m.id === monthId)?.fullName || '',
      level,
      levelLabel,
      details: {
        inorm,
        comprehension,
        totalReadings: monthData.ld || 0,
        readingDays: monthData.dl || 0
      }
    };
  };

  // Color de la insignia según nivel
  const getBadgeColor = (level) => {
    switch (level) {
      case 'destacado':
        return 'bg-gradient-to-br from-green-400 to-emerald-600 text-white';
        return 'bg-gradient-to-br from-yellow-400 to-amber-500 text-amber-900';
      case 'logrado':
        return 'bg-gradient-to-br from-blue-400 to-indigo-600 text-white';
        return 'bg-gradient-to-br from-green-400 to-emerald-600 text-white';
      case 'progreso':
        return 'bg-gradient-to-br from-amber-300 to-orange-500 text-white';
        return 'bg-gradient-to-br from-blue-400 to-indigo-600 text-white';
      case 'inicio':
        return 'bg-gradient-to-br from-orange-300 to-red-500 text-white';
      case 'futuro':
        return 'bg-gray-200 text-gray-400';
      case 'sin-datos':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-gray-300 text-gray-700';
    }
  };

  // Manejar click
  const handleBadgeClick = (monthId) => {
    const monthData = getMonthData(monthId);
    
    if (monthData.isFuture || !monthData.hasData) return;

    setSelectedMonth(monthData);
    setShowDetails(!showDetails);
  };

  // SVG para la corona
  const CrownIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="currentColor" 
      className="w-3 h-3 text-yellow-800"
    >
      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
    </svg>
  );

  return (
    <div className="p-4">
      {/* Contenedor horizontal con scroll */}
      <div className="flex overflow-x-auto pb-4 space-x-3 scrollbar-hide">
        {months.map((month) => {
          const monthData = getMonthData(month.id);
          const colorClass = getBadgeColor(monthData.level);

          return (
            <motion.div
              key={month.id}
              className={`flex-shrink-0 relative flex flex-col items-center justify-center rounded-full w-16 h-16 cursor-pointer ${colorClass}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleBadgeClick(month.id)}
              title={monthData.isFuture ? 
                `${monthData.monthName} (Próximo)` : 
                monthData.hasData ? 
                  `${monthData.monthName}: ${monthData.levelLabel}` : 
                  `${monthData.monthName}: Sin datos`}
            >
              {/* Coronita para destacado */}
              {monthData.level === 'destacado' && (
                <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
                  <CrownIcon />
                </div>
              )}
              
              <div className="text-sm font-bold">{month.name}</div>
              
              {!monthData.isFuture && monthData.hasData && (
                <>
                  <div className="text-lg font-bold">
                    {(monthData.overallScore * 100).toFixed(0)}%
                  </div>
                  <div className="text-[10px] font-semibold mt-[-2px] text-center px-1 leading-tight">
                    {monthData.levelLabel.split(' ').map((word, i) => (
                      <div key={i}>{word}</div>
                    ))}
                  </div>
                </>
              )}
              
              {monthData.isFuture && (
                <div className="text-xs">Próximo</div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Detalles del mes seleccionado */}
      <AnimatePresence>
        {showDetails && selectedMonth && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-white rounded-xl shadow-lg p-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold">
                  {selectedMonth.monthName} 2025
                </h3>
                
                {/* Etiqueta de nivel */}
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  selectedMonth.level === 'destacado' ? 'bg-green-100 text-green-800' :
                  selectedMonth.level === 'logrado' ? 'bg-blue-100 text-blue-800' :
                  selectedMonth.level === 'progreso' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-amber-100 text-red-800'
                }`}>
                  {selectedMonth.levelLabel}
                  {selectedMonth.level === 'destacado' && (
                    <CrownIcon className="inline-block ml-1" />
                  )}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-600">
                    {(selectedMonth.details.inorm * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-blue-800">Hábito</div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-600">
                    {(selectedMonth.details.comprehension * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-green-800">Comprensión</div>
                </div>

                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-amber-600">
                    {selectedMonth.details.readingDays}
                  </div>
                  <div className="text-xs text-amber-800">Días activo</div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-purple-600">
                    {selectedMonth.details.totalReadings}
                  </div>
                  <div className="text-xs text-purple-800">Lecturas</div>
                </div>
                
                
              </div>
              
              {/* Barra de progreso */}
              <div className="mt-4">
                <div className="flex justify-between text-xs mb-1">
                  <span>Progreso: {selectedMonth.levelLabel}</span>
                  <span>{(selectedMonth.overallScore * 100).toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      selectedMonth.level === 'destacado' ? 'bg-green-500' :
                      selectedMonth.level === 'logrado' ? 'bg-blue-500' :
                      selectedMonth.level === 'progreso' ? 'bg-yellow-500' :
                      'bg-amber-500'
                    }`}
                    style={{ width: `${selectedMonth.overallScore * 100}%` }}
                  ></div>
                </div>
                
                {/* Marcadores de niveles */}
                <div className="flex justify-between mt-1 text-[10px]">
                  <span>0%</span>
                  <span>20%</span>
                  <span>40%</span>
                  <span>60%</span>
                  <span>80%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HorizontalMonthlyStats;